<script setup lang="ts">
/**
 * Step 4 du checkout événement :
 * récap billets + total + don asso optionnel + soutien Donoor + breakdown,
 * zone Stripe (Express + Card) + PayPal info card, mention des conditions
 * (acceptées en validant), bouton payer.
 */
import type { useCheckout } from "@donoor/checkout";
import type { Event, TicketType, TicketOption } from "~/types/event";
import { dernierJourPlusieursFoisBillet, echeancierDuPanier, jourCivilParis } from "@donoor/utils";
import { fraisDesParts, suiteIdentique } from "~/utils/plusieursFois";
import PublicPageTotauxPlusieursFois from "~/components/public-page/PublicPageTotauxPlusieursFois.vue";

const { t } = useI18n();
const { formatCurrency } = useFormatLangue();

interface AttendeeLocal {
  firstName: string;
  lastName: string;
  email: string;
  ticketTypeId: string;
  ticketTypeName: string;
  selectedOptions: Record<string, number>;
}

interface CalculationsView {
  feesAmount: { value: number };
  finalAmount: { value: number };
  amountWithFees: { value: number };
  formatAmount: (a: number) => string;
}

const props = defineProps<{
  event: Event;
  attendees: AttendeeLocal[];
  ticketTypes: TicketType[];
  ticketOptions: TicketOption[];
  totalPrice: number;
  ticketsOnlyPrice: number;
  optionsTotal: number;
  promoCode: string;
  promoLoading: boolean;
  promoError: string;
  promoApplied: { code: string; discount_type: string; discount_value: number } | null;
  promoDiscount: number;
  showDonation: boolean;
  showDonationInput: boolean;
  donationAmount: number;
  includeFees: boolean;
  donateUs: boolean;
  donateToUsAmount: number;
  hasFees: boolean;
  calculations: CalculationsView;
  isPreview: boolean;
  needsPayment: boolean;
  /** Instances Stripe + PayPal pour DonateExpressCheckoutSection. */
  stripeCheckout: ReturnType<typeof useCheckout>;
  paypalCheckout: ReturnType<typeof useCheckout>;
  paymentForm: { isSubscription: Ref<boolean> };
  /** Provider primaire (Karim 2026-06-21). */
  usesPaypalCard: boolean;
  usesPaypalWallets: boolean;
  isPaypalOnlyMode: boolean;
  showPayPalAlternative: boolean;
  stripeChargesEnabled: boolean;
  paypalChargesEnabled: boolean;
  checkoutIsInitialized: boolean;
  checkoutError: { message?: string } | null;
  orderError: string | null;
  orderErrorTechnical: boolean;
  isProcessing: boolean;
  /** Paiement en plusieurs fois : nombre choisi (0 = en une fois), commission (décimal). */
  nombreParts?: number;
  tauxCommission?: number;
}>();

defineEmits<{
  (e: 'update:show-donation-input' | 'update:include-fees', v: boolean): void;
  (e: 'update:donation-amount' | 'update:donate-to-us-amount' | 'update:nombre-parts', v: number): void;
  (e: 'update:promo-code', v: string): void;
  (e: 'apply-promo' | 'remove-promo' | 'retry-stripe-init' | 'clear-order-error' | 'back' | 'submit'): void;
}>();

/** Montant à payer en une fois (frais couverts ou non). */
const totalUneFois = computed(() =>
  props.includeFees
    ? props.calculations.amountWithFees.value
    : props.totalPrice + props.donationAmount + (props.donateUs ? props.donateToUsAmount : 0),
);

/** Débité AUJOURD'HUI : la 1re part en plusieurs fois (calcul du serveur), sinon le total. */
/** Parts du paiement en plusieurs fois choisi (vide en une fois), calcul du serveur. */
const partsChoisies = computed(() => {
  const n = props.nombreParts ?? 0;
  if (n < 2) return [];
  return echeancierDuPanier({
      montantDiviseEuros: props.totalPrice,
      nombre: n,
      premierJour: jourCivilParis(),
      enPlusEuros: props.donationAmount,
      soutienEuros: props.donateUs ? props.donateToUsAmount : 0,
      tauxCommission: props.tauxCommission ?? 0,
      fraisCouverts: props.includeFees,
    });
});

const aPayerAujourdhui = computed(() => partsChoisies.value[0]?.montant ?? totalUneFois.value);

/** Frais affichés : en plusieurs fois, ceux de TOUTE la commande (un par paiement). */
const fraisAffiches = computed(() =>
  partsChoisies.value.length >= 2
    ? fraisDesParts(partsChoisies.value, props.totalPrice + props.donationAmount + (props.donateUs ? props.donateToUsAmount : 0))
    : props.calculations.feesAmount.value,
);

/** « puis 2 × 20 € » (ou « puis 2 prélèvements mensuels ») sous le bouton Payer. */
const suitePaiement = computed(() => {
  const parts = partsChoisies.value;
  if (parts.length < 2) return "";
  const suite = suiteIdentique(parts);
  return suite
    ? t("plusieursFois.choix.puisNFois", { n: suite.nombre, montant: formatCurrency(suite.montant) })
    : t("plusieursFois.choix.puisNPrelevements", { n: parts.length - 1 });
});

/** Widget intégré chez l'association (`?embed=1`) : pas de colonne de droite. */
const routeRecap = useRoute();
const estIntegre = computed(() => routeRecap.query.embed === "1" || routeRecap.query.embed === "true");
</script>

<template>
  <div class="p-3 md:p-5">
    <h3 class="text-base font-bold text-[#2d2e5c] dark:text-white mb-5">{{ t('billetterie.bslugStepRecap.titre') }}</h3>

    <PublicPageOrderErrorBanner
      v-if="orderError"
      :error="orderError"
      :technical="orderErrorTechnical"
      :contact-email="event?.contact_email"
      @clear="$emit('clear-order-error')"
    />

    <!-- Billets -->
    <div class="rounded-2xl border border-gray-200 dark:border-[#262626] overflow-hidden mb-4">
      <div class="px-5 py-3 bg-gray-50 dark:bg-[#0f0f0f] border-b border-gray-100 dark:border-[#1e1e1e]">
        <p class="text-xs font-semibold text-[#717198] dark:text-gray-500 uppercase tracking-wide">{{ t('billetterie.bslugStepRecap.nParticipants', attendees.length) }}</p>
      </div>
      <div class="divide-y divide-gray-100 dark:divide-[#1e1e1e]">
        <div v-for="(a, idx) in attendees" :key="idx" class="px-5 py-4">
          <div class="flex items-start gap-3">
            <div class="w-8 h-8 rounded-full flex items-center justify-center shrink-0" :style="{ background: 'var(--accent, #212121)' }">
              <span class="text-xs font-bold" :style="{ color: 'var(--accent-text, #fff)' }">{{ idx + 1 }}</span>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-[#2d2e5c] dark:text-white truncate">{{ a.firstName }} {{ a.lastName }}</p>
              <p class="text-xs text-[#717198] dark:text-gray-500 truncate">{{ a.email }}</p>
              <div class="flex items-center justify-between mt-2">
                <span class="text-xs text-[#505870] dark:text-gray-400 flex items-center gap-1">
                  <UIcon name="i-lucide-ticket" class="size-3" />
                  {{ a.ticketTypeName }}
                </span>
                <span class="text-xs font-semibold text-[#2d2e5c] dark:text-white">
                  {{ Number(ticketTypes.find((t: TicketType) => t.id === a.ticketTypeId)?.price || 0) > 0 ? formatCurrency(Number(ticketTypes.find((t: TicketType) => t.id === a.ticketTypeId)?.price)) : t('billetterie.partage.gratuit') }}
                </span>
              </div>
              <template v-for="(qty, optId) in a.selectedOptions" :key="optId">
                <div v-if="qty > 0" class="flex items-center justify-between mt-1">
                  <span class="text-xs text-[#717198] dark:text-gray-400 flex items-center gap-1">
                    <UIcon name="i-lucide-plus-circle" class="size-3" />
                    {{ ticketOptions.find((o: TicketOption) => o.id === optId)?.name }}{{ qty > 1 ? ` ×${qty}` : '' }}
                  </span>
                  <span class="text-xs text-[#717198] dark:text-gray-400 font-medium">
                    {{ Number(ticketOptions.find((o: TicketOption) => o.id === optId)?.price || 0) > 0 ? '+' + formatCurrency(Number(ticketOptions.find((o: TicketOption) => o.id === optId)?.price) * qty) : t('billetterie.partage.gratuit') }}
                  </span>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Don à l'association -->
    <div v-if="showDonation" class="mt-5 rounded-2xl border border-gray-200 dark:border-[#262626] p-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <UIcon name="i-lucide-heart" class="size-4 text-(--color-main)" />
          <span class="text-sm font-medium text-[#2d2e5c] dark:text-gray-300">
            {{ t('billetterie.bslugStepRecap.faireUnDonA', { association: event.organization_name || t('billetterie.partage.associationParDefaut') }) }}
          </span>
        </div>
        <USwitch
          :model-value="showDonationInput"
          size="xs"
          :ui="{ base: 'data-[state=checked]:bg-(--color-main)!' }"
          @update:model-value="(v: boolean) => {
            $emit('update:show-donation-input', v);
            if (v) {
              if (donationAmount === 0) $emit('update:donation-amount', 5);
              $emit('update:include-fees', true);
            }
            if (!v) $emit('update:donation-amount', 0);
          }"
        />
      </div>
      <Transition enter-active-class="transition-all duration-300 ease-out" enter-from-class="opacity-0 max-h-0" enter-to-class="opacity-100 max-h-32" leave-active-class="transition-all duration-200 ease-in" leave-from-class="opacity-100 max-h-32" leave-to-class="opacity-0 max-h-0">
        <div v-if="showDonationInput" class="overflow-hidden mt-3 space-y-2">
          <p class="text-xs text-[#717198] dark:text-gray-500">{{ t('billetterie.bslugStepRecap.texteDonLibre') }}</p>
          <!-- Grille de 4 colonnes égales (Karim 2026-09-26) : les montants
               en `flex-1` et « Autre » en `w-24` laissaient trop peu de place
               pour saisir un montant. -->
          <div class="grid grid-cols-4 gap-2">
              <button v-for="preset in [5, 10, 20]" :key="preset" type="button" class="py-2 rounded-xl text-sm font-medium border transition-colors" :class="donationAmount === preset ? 'accent-btn border-transparent' : 'border-gray-200 dark:border-[#262626] text-[#2d2e5c] dark:text-gray-300 hover:border-gray-300'" @click="$emit('update:donation-amount', donationAmount === preset ? 0 : preset)">
                {{ formatCurrency(preset) }}
              </button>
            <div class="relative min-w-0">
              <input
                :value="donationAmount"
                type="number"
                min="0"
                step="1"
                :placeholder="t('billetterie.bslugStepRecap.placeholderAutre')"
                class="w-full pl-3 pr-7 py-2 rounded-xl border border-gray-200 dark:border-[#262626] bg-white dark:bg-[#141414] text-sm text-right font-medium text-[#2d2e5c] dark:text-white focus:outline-none focus:border-[#212121] dark:focus:border-white transition-colors hide-arrows"
                @input="$emit('update:donation-amount', Number(($event.target as HTMLInputElement).value))"
              />
              <span class="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">€</span>
            </div>
          </div>
        </div>
      </Transition>
    </div>

    <!-- Soutenir Donoor -->
    <div class="mt-5 rounded-xl border border-gray-200 dark:border-[#262626] px-3 py-2.5">
      <DonateSupportDonoor
        :donate-to-us-amount="donateToUsAmount"
        :format-amount="calculations.formatAmount"
        :organization-name="event?.organization_name"
        @update:donate-to-us-amount="$emit('update:donate-to-us-amount', $event)"
      />
    </div>

    <template v-if="needsPayment">
      <div class="mt-3">
        <div class="rounded-xl border border-gray-200 dark:border-[#262626] px-3 py-2.5">
          <DonateFeesOption
            :include-fees="includeFees"
            :fees-amount="calculations.feesAmount.value"
            :final-amount="calculations.finalAmount.value"
            :format-amount="calculations.formatAmount"
            :fees-text="hasFees ? t('billetterie.bslugStepRecap.fraisStripeEtDonoor') : 'Stripe'"
            :provider-label="usesPaypalCard ? 'paypal' : 'stripe'"
            @update:include-fees="$emit('update:include-fees', $event)"
          />
        </div>
      </div>

      <!-- Paiement en plusieurs fois (Karim 2026-10-21) : carte uniquement. -->
      <ChoixPaiementPlusieursFois
        :reglage="event.installments_counts"
        :dernier-jour="dernierJourPlusieursFoisBillet(event.start_date)"
        :montant-divise="totalPrice"
        :en-plus="donationAmount"
        :soutien="donateUs ? donateToUsAmount : 0"
        :taux-commission="tauxCommission ?? 0"
        :frais-couverts="includeFees"
        :total-une-fois="totalUneFois"
        :indisponible="usesPaypalCard || isPaypalOnlyMode || isPreview"
        :model-value="nombreParts ?? 0"
        @update:model-value="$emit('update:nombre-parts', $event)"
      />

      <!-- Code promo : sur ordinateur il est déjà dans le détail à droite
           (Karim 2026-10-21) — affiché ici seulement sur téléphone (le
           détail de droite y est masqué) et dans le widget intégré (pas de
           colonne de droite). -->
      <div v-if="(ticketsOnlyPrice + optionsTotal) > 0" class="mt-3" :class="estIntegre ? '' : 'lg:hidden'">
        <PublicPagePromoCodeInput
          variant="a"
          :model-value="promoCode"
          :applied="promoApplied"
          :loading="promoLoading"
          :error="promoError"
          @update:model-value="$emit('update:promo-code', $event)"
          @apply="$emit('apply-promo')"
          @remove="$emit('remove-promo')"
        />
      </div>

      <!-- Détail des montants : sur ordinateur il est déjà dans la colonne de
           droite (Karim 2026-10-24 : « ça sert à rien, il est déjà à droite ») —
           affiché ici seulement sur téléphone et dans le widget intégré. -->
      <div class="mt-4 rounded-2xl bg-gray-50 dark:bg-[#0f0f0f] border border-gray-200 dark:border-[#262626] p-4 space-y-2" :class="estIntegre ? '' : 'lg:hidden'">
        <div class="flex justify-between text-sm">
          <span class="text-[#717198]">{{ t('billetterie.partage.billets') }}</span>
          <span class="text-[#2d2e5c] dark:text-white font-medium">{{ formatCurrency(ticketsOnlyPrice) }}</span>
        </div>
        <div v-if="optionsTotal > 0" class="flex justify-between text-sm">
          <span class="text-[#717198]">{{ t('billetterie.partage.options') }}</span>
          <span class="text-[#2d2e5c] dark:text-white font-medium">{{ formatCurrency(optionsTotal) }}</span>
        </div>
        <div v-if="promoDiscount > 0" class="flex justify-between text-sm">
          <span class="text-[#717198]">{{ t('billetterie.bslugStepRecap.codePromoAvecCode', { code: promoApplied?.code }) }}</span>
          <span class="text-emerald-600 dark:text-emerald-400 font-medium">-{{ formatCurrency(promoDiscount) }}</span>
        </div>
        <div v-if="donationAmount > 0" class="flex justify-between text-sm">
          <span class="text-[#717198]">{{ t('billetterie.bslugSidebar.donA', { association: event.organization_name || t('billetterie.partage.associationParDefaut') }) }}</span>
          <span class="text-[#2d2e5c] dark:text-white font-medium">{{ formatCurrency(donationAmount) }}</span>
        </div>
        <div v-if="fraisAffiches > 0 && includeFees" class="flex justify-between text-sm">
          <span class="text-[#717198]">{{ t('billetterie.partage.fraisTransaction') }}</span>
          <span class="text-[#2d2e5c] dark:text-white">{{ formatCurrency(fraisAffiches) }}</span>
        </div>
        <div v-if="donateUs && donateToUsAmount > 0" class="flex justify-between text-sm">
          <span class="text-[#717198]">{{ t('billetterie.partage.soutienDonoor') }}</span>
          <span class="text-[#2d2e5c] dark:text-white">{{ formatCurrency(donateToUsAmount) }}</span>
        </div>
        <div v-if="partsChoisies.length >= 2" class="pt-2 border-t border-gray-200 dark:border-[#262626]">
          <PublicPageTotauxPlusieursFois :parts="partsChoisies" />
        </div>
        <div v-else class="flex justify-between pt-2 border-t border-gray-200 dark:border-[#262626]">
          <span class="text-sm font-semibold text-[#2d2e5c] dark:text-white">{{ (nombreParts ?? 0) >= 2 ? t('plusieursFois.choix.aPayerAujourdhui') : t('billetterie.partage.totalAPayer') }}</span>
          <span class="text-lg font-bold text-[#2d2e5c] dark:text-white">
            {{ calculations.amountWithFees.value > 0 ? formatCurrency(aPayerAujourdhui) : t('billetterie.partage.gratuit') }}
          </span>
        </div>
      </div>
    </template>

    <template v-if="!isPreview && needsPayment">
      <!-- ─── Zone paiement unifiée (Karim 2026-06-21) ───────────
           Pas de toggle Stripe/PayPal : le provider est imposé par
           `payment_providers.preferred_provider`.
             usesPaypalCard=true  → CardFields PayPal + wallets PayPal
             usesPaypalCard=false → Stripe Elements + wallets Stripe
           + bouton PayPal alternatif si `showPayPalAlternative`. -->

      <!-- Express Checkout (wallets primaire + bouton PayPal alt). Masqué en
           plusieurs fois : seul le champ carte enregistre la carte. -->
      <div v-if="(nombreParts ?? 0) < 2" class="mt-4">
        <DonateExpressCheckoutSection
          :stripe-checkout="stripeCheckout"
          :paypal-checkout="paypalCheckout"
          :payment-form="paymentForm"
          :is-paypal-only-mode="isPaypalOnlyMode"
          :show-pay-pal-alternative="showPayPalAlternative"
          :is-subscription="false"
          :uses-paypal-wallets="usesPaypalWallets"
          paypal-container-id="paypal-alt-buttons-b-page"
        />
      </div>

      <div class="mt-4">
        <div v-if="(nombreParts ?? 0) < 2" class="relative my-4 flex items-center">
          <div class="flex-1 border-t border-gray-200 dark:border-[#262626]" />
          <span class="px-3 text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-500">{{ t('billetterie.bslugStepRecap.ouPayerParCarte') }}</span>
          <div class="flex-1 border-t border-gray-200 dark:border-[#262626]" />
        </div>

        <div v-if="!usesPaypalCard && checkoutError" class="rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20 p-4">
          <div class="flex items-start gap-3">
            <UIcon name="i-lucide-alert-triangle" class="size-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
            <div class="flex-1">
              <p class="text-sm font-semibold text-red-700 dark:text-red-300">{{ t('billetterie.bslugStepRecap.paiementIndisponible') }}</p>
              <p class="text-xs text-red-600 dark:text-red-400 mt-0.5">{{ checkoutError.message || t('billetterie.bslugStepRecap.verifiezConnexion') }}</p>
            </div>
          </div>
          <button
            class="mt-3 w-full px-4 py-2 rounded-xl text-xs font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors"
            @click="$emit('retry-stripe-init')"
          >
            {{ t('commun.actions.reessayer') }}
          </button>
        </div>

        <div v-else class="rounded-xl border border-gray-200 dark:border-[#262626] bg-white dark:bg-[#0f0f0f] p-4">
          <DonateCardElement
            :is-initialized="usesPaypalCard ? (paypalCheckout.cardMounted.value || !!paypalCheckout.cardFormError.value) : (stripeCheckout.cardMounted.value || !!stripeCheckout.cardFormError.value)"
            :provider="usesPaypalCard ? 'paypal' : 'stripe'"
            :mount-error-code="
              usesPaypalCard ? (paypalCheckout.cardFormError.value?.code ?? null) : null
            "
          />
        </div>
      </div>
    </template>

    <div v-if="isPreview" class="mt-5 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800/50">
      <div class="flex items-center gap-3">
        <UIcon name="i-lucide-eye" class="size-5 text-amber-600 dark:text-amber-400 shrink-0" />
        <p class="text-sm text-amber-800 dark:text-amber-300">{{ t('billetterie.bslugStepRecap.modeApercu') }}</p>
      </div>
    </div>

    <!-- Conditions : acceptées EN VALIDANT (Karim 2026-09-29), comme la page
         de don — plus de case à cocher ni de boutons de paiement bloqués. -->
    <PublicPageMentionConditions v-if="!isPreview" :paiement="needsPayment" />

    <!-- Téléphone : « Payer » sur toute la largeur, « Retour » dessous —
         sur une ligne partagée, le montant passait à la ligne (Karim 2026-10-24). -->
    <div class="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between mt-5 pt-4 border-t border-gray-100 dark:border-gray-800">
      <button class="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#1a1a1a] hover:bg-gray-200 dark:hover:bg-[#262626] transition-colors" @click="$emit('back')">
        <UIcon name="i-lucide-arrow-left" class="size-4" /> {{ t('commun.actions.retour') }}
      </button>
      <button
        v-if="!isPreview"
        data-testid="btn-submit-order"
        class="w-full sm:w-auto px-6 sm:px-8 py-3 accent-btn rounded-xl text-sm font-semibold transition-colors disabled:opacity-40 flex items-center justify-center gap-2.5"
        :disabled="
          isProcessing ||
          (needsPayment && !usesPaypalCard && (!checkoutIsInitialized || !!checkoutError)) ||
          (needsPayment && usesPaypalCard && !paypalCheckout.cardMounted.value)
        "
        @click="$emit('submit')"
      >
        <UIcon v-if="isProcessing" name="i-lucide-loader-2" class="size-4 animate-spin" />
        <UIcon v-else name="i-lucide-lock" class="size-4" />
        <span class="flex flex-col items-center sm:items-start leading-tight">
          <span class="whitespace-nowrap text-[15px] sm:text-sm">{{ isProcessing ? t('billetterie.bslugStepRecap.traitementEnCours') : !needsPayment ? t('billetterie.bslugStepRecap.confirmerReservation') : suitePaiement ? t('plusieursFois.choix.payerAujourdhui', { montant: formatCurrency(aPayerAujourdhui) }) : t('billetterie.bslugStepRecap.payerMontant', { montant: formatCurrency(aPayerAujourdhui) }) }}</span>
          <span v-if="suitePaiement && !isProcessing && needsPayment" class="mt-0.5 text-xs sm:text-[11px] font-normal opacity-80 whitespace-nowrap">{{ suitePaiement }}</span>
        </span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.accent-btn {
  background: var(--accent, #212121);
  color: var(--accent-text, #fff);
}
.accent-btn:hover {
  opacity: 0.9;
}
.hide-arrows::-webkit-outer-spin-button,
.hide-arrows::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.hide-arrows {
  -moz-appearance: textfield;
}
</style>
